import { ErrorOutline } from '@mui/icons-material';
import { Box, CircularProgress, Divider, Link, Typography, useTheme } from '@mui/material';
import type { FC } from 'react';

import { ASYNC_JOB_FAILED_ITEMS_PREVIEW_LIMIT } from '@core/constants/common';

import { SquaredBadge } from '../SquaredBadge/SquaredBadge';
import type { AsyncJobFailedItem } from './AsyncJobFailedItemsModal';

type Props = {
  actionLabel: string;
  failedCount: number;
  items: AsyncJobFailedItem[];
  isLoading: boolean;
  isError: boolean;
  onViewFailures: () => void;
};

export const AsyncJobFailedItemsPreview: FC<Props> = ({
  actionLabel,
  failedCount,
  items,
  isLoading,
  isError,
  onViewFailures,
}) => {
  const theme = useTheme();
  const previewItems = items.slice(0, ASYNC_JOB_FAILED_ITEMS_PREVIEW_LIMIT);
  const remainingCount = Math.max(failedCount - previewItems.length, 0);

  if (failedCount === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 1.5,
        borderTop: `1px solid ${theme.palette.SlabGray.Stroke}`,
        pt: 1.5,
      }}
    >
      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} thickness={5} sx={{ color: theme.palette.SlabYellow[800] }} />
          <Typography variant='body2' color='text.secondary'>
            Loading failure details...
          </Typography>
        </Box>
      )}

      {!isLoading && !isError && previewItems.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {previewItems.map((item, index) => (
            <Box key={item.id}>
              <Box sx={{ display: 'flex', gap: 1, py: 1 }}>
                <ErrorOutline
                  sx={{ fontSize: 16, color: theme.palette.SlabYellow[800], mt: 0.25 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='body2' fontWeight={600} noWrap>
                    {item.primaryText}
                  </Typography>
                  {item.secondaryText !== undefined && (
                    <Typography variant='body2' color='text.secondary' sx={{ fontSize: '0.8rem' }}>
                      {item.secondaryText}
                    </Typography>
                  )}
                  {item.reason !== undefined && (
                    <Typography
                      variant='body2'
                      sx={{
                        mt: 0.5,
                        color: theme.palette.SlabRed[600],
                        fontSize: '0.8rem',
                      }}
                    >
                      {item.reason}
                    </Typography>
                  )}
                </Box>
              </Box>
              {index < previewItems.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>
      )}

      {!isLoading && (isError || (failedCount > 0 && previewItems.length === 0)) && (
        <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
          Unable to load failure details.
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
        {remainingCount > 0 ? (
          <Typography variant='body2' color='text.secondary'>
            {remainingCount} more failed {remainingCount === 1 ? 'item' : 'items'}
          </Typography>
        ) : (
          <Box />
        )}

        <Link
          component='button'
          underline='none'
          onClick={onViewFailures}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            color: theme.palette.SlabGray[800],
            '&:hover': {
              color: theme.palette.SlabGray[600],
            },
          }}
        >
          <Typography variant='body2' fontWeight={500}>
            {actionLabel}
          </Typography>
          <SquaredBadge
            badgeContent={failedCount}
            textColor='SlabGray.900'
            backgroundColor='SlabYellow.600'
          />
        </Link>
      </Box>
    </Box>
  );
};
